import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const normalizeInvoice = (inv) => ({
    ...inv,
    fileUid: inv.file?.uid ?? null,
    paymentProofFileUid: inv.payment_proof_file?.uid ?? null,
    // Extract embedded service context (present in global GET /v1/invoices response)
    serviceUid: inv.service?.uid ?? inv.serviceUid ?? null,
    supplierUid: inv.service?.supplier_uid ?? inv.supplierUid ?? null,
    serviceName: inv.service?.name ?? inv.serviceName ?? null,
    serviceType: inv.service?.type ?? inv.serviceType ?? null,
});

export const useAllInvoices = (filters = {}) => {
    return useInfiniteQuery({
        queryKey: ['all-invoices', filters],
        queryFn: async ({ pageParam }) => {
            const params = { ...filters };
            if (pageParam) params.cursor = pageParam;
            const page = await api.getAllInvoices(params);
            return { ...page, items: page.items.map(normalizeInvoice) };
        },
        getNextPageParam: (lastPage) =>
            lastPage.pagination?.has_more_pages ? lastPage.pagination.next_cursor : undefined,
        initialPageParam: null,
    });
};

export const useInvoicesByService = (serviceUid, filters = {}) => {
    return useInfiniteQuery({
        queryKey: ['invoices', { serviceUid }, filters],
        queryFn: async ({ pageParam }) => {
            const params = { ...filters };
            if (pageParam) params.cursor = pageParam;
            const page = await api.getInvoicesByService(serviceUid, params);
            return { ...page, items: page.items.map(normalizeInvoice) };
        },
        getNextPageParam: (lastPage) =>
            lastPage.pagination?.has_more_pages ? lastPage.pagination.next_cursor : undefined,
        initialPageParam: null,
        enabled: !!serviceUid,
    });
};

export const useCreateInvoice = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceUid, data }) =>
            api.createInvoice(serviceUid, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['all-invoices'] });
        },
    });
};

export const useUpdateInvoice = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ invoiceUid, data }) =>
            api.updateInvoice(invoiceUid, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['all-invoices'] });
        },
    });
};

export const useDeleteInvoice = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ invoiceUid }) =>
            api.deleteInvoice(invoiceUid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['all-invoices'] });
        },
    });
};
